// AppLayout.jsx - Main layout component with unified navigation
import React from 'react';
import { useLocation } from 'react-router-dom';
import UnifiedNavigation from './UnifiedNavigation';
import DemoWatermark from './DemoWatermark';
import { useDevToolsDetection } from '../../hooks/useDevToolsDetection';

const AppLayout = ({ children, currentApp = 'foundations' }) => {
  const location = useLocation();
  const isViewer = localStorage.getItem('userRole') === 'demo_viewer';
  const demoEmail = sessionStorage.getItem('userEmail') || localStorage.getItem('userEmail') || '';
  const devToolsOpen = useDevToolsDetection(isViewer);
  
  // Don't show navigation on login page
  if (location.pathname === '/login') {
    return children;
  }

  // Determine current app and page for navigation highlighting
  const getCurrentApp = () => {
    const path = location.pathname;
    if (path.includes('/compass') || path.includes('/intelligence')) return 'compass';
    if (path.includes('/defense') || path.includes('/studio')) return 'defense';
    return 'foundations';
  };
  
  const getCurrentPage = () => {
    const path = location.pathname;
    if (path.includes('/coordinator')) return 'coordinator';
    if (path.includes('/portfolio')) return 'portfolio';
    if (path.includes('/admin')) return 'admin';
    if (path.includes('/assessment')) return 'assessment';
    // Check specific intelligence sub-routes before the general match
    if (path.includes('/intelligence/investment')) return 'investment-optimization';
    if (path.includes('/intelligence/scenarios')) return 'strategic-scenarios';
    if (path.includes('/intelligence/outside-pressures')) return 'outside-pressures';
    if (path.includes('/intelligence/reports')) return 'board-reports';
    if (path.includes('/intelligence')) return 'business-intelligence';
    if (path.includes('/studio')) return 'friction';
    return '';
  };

  const app = currentApp || getCurrentApp();

  return (
    <div className="flex min-h-screen" style={{ background: 'var(--ra-navy)' }}>
      {/* Skip navigation link — first focusable element */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-[9999] focus:top-4 focus:left-4 focus:px-4 focus:py-2 focus:rounded-lg focus:text-white focus:font-semibold"
        style={{ background: 'var(--ra-gradient-cta)' }}
      >
        Skip to main content
      </a>

      {/* Unified Navigation Sidebar */}
      <UnifiedNavigation
        currentApp={app}
        currentPage={getCurrentPage()}
      />

      {/* Main Content Area */}
      <main
        id="main-content"
        className="flex-1 min-h-screen overflow-auto"
        style={devToolsOpen ? { filter: 'blur(8px)', transition: 'filter 0.3s' } : undefined}
      >
        {/* DevTools detection warning */}
        {isViewer && devToolsOpen && (
          <div style={{
            margin: '1rem 1.5rem 0',
            padding: '0.75rem 1rem',
            borderRadius: '8px',
            background: 'linear-gradient(90deg, rgba(255,82,82,0.15) 0%, rgba(255,152,0,0.1) 100%)',
            border: '1px solid rgba(255,82,82,0.4)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
          }}>
            <span style={{ fontSize: '1.1rem' }} aria-hidden="true">&#9888;</span>
            <span style={{ color: '#ff8a80', fontWeight: 500, fontSize: '0.85rem' }}>
              Developer tools detected. Some content has been obscured for security.
            </span>
          </div>
        )}

        {/* Demo mode banner */}
        {isViewer && (
          <div style={{
            margin: '1rem 1.5rem 0',
            padding: '0.6rem 1rem',
            borderRadius: '8px',
            background: 'linear-gradient(90deg, rgba(0,217,255,0.12) 0%, rgba(100,255,218,0.08) 100%)',
            border: '1px solid rgba(0,217,255,0.25)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <span style={{ color: '#64ffda', fontWeight: 500, fontSize: '0.85rem' }}>
              Demo Mode — You are viewing a read-only preview of Foundations Risk Aperture
            </span>
            <span style={{
              background: 'rgba(0,217,255,0.2)',
              color: '#00d9ff',
              fontWeight: 700,
              letterSpacing: '0.1em',
              fontSize: '0.7rem',
              padding: '0.2rem 0.6rem',
              borderRadius: '4px',
            }}>
              DEMO
            </span>
          </div>
        )}
        {children}
      </main>

      {/* Watermark overlay for demo viewers */}
      {isViewer && <DemoWatermark email={demoEmail} />}
    </div>
  );
};

export default AppLayout;